// src/services/NotificationListener.js - Smart Socket Response Router
import useNotificationStore from '../stores/NotificationStore';

/**
 * NotificationListener
 *
 * The brain of the notification system. Listens to ALL socket responses
 * and intelligently decides:
 * - Should we show a notification for this event?
 * - What type should it be?
 * - What message should we display?
 * - How long should it stay visible?
 *
 * Uses PATTERN MATCHING instead of hardcoding every event.
 * This means new events automatically work without code changes!
 */
class NotificationListener {
  constructor(socketManager) {
    this.socketManager = socketManager;
    this.notificationStore = useNotificationStore.getState();
    this.isInitialized = false;

    // Debug mode
    this.debug = import.meta.env.VITE_DEBUG_NOTIFICATIONS === 'true';
  }

  /**
   * Initialize the listener - called once when socket authenticates
   */
  initialize() {
    if (this.isInitialized) {
      this.log('⚠️ NotificationListener already initialized');
      return;
    }

    if (!this.socketManager?.universalHandler) {
      console.error('❌ NotificationListener: No universal handler available');
      return;
    }

    this.log('🎧 NotificationListener: Setting up global socket response listener');

    // Listen to ALL socket responses
    this.socketManager.universalHandler.on('response', this.handleResponse.bind(this));

    this.isInitialized = true;
    this.log('✅ NotificationListener: Initialized successfully');
  }

  /**
   * Main handler for ALL socket responses
   * @param {object} response - Socket response object
   */
  handleResponse(response) {
    const { status, event, message, data } = response;

    this.log(`📨 Socket response received:`, { status, event });

    // Step 1: Should we show a notification?
    if (!this.shouldNotify(event, status)) {
      this.log(`⏭️ Skipping notification for: ${event}`);
      return;
    }

    // Step 2: Determine notification type
    const type = this.getNotificationType(status, event);

    // Step 3: Format the message
    const notificationMessage = this.formatMessage(event, message, data);

    // Step 4: Determine duration
    const duration = this.getDuration(type);

    // Step 5: Get title (if applicable)
    const title = this.getTitle(event, type);

    // Step 6: Add to notification queue
    this.notificationStore.add({
      type,
      title,
      message: notificationMessage,
      duration,
      timestamp: Date.now()
    });

    this.log(`✅ Notification shown:`, { type, title, message: notificationMessage });
  }

  /**
   * PATTERN MATCHING: Determine if event should trigger a notification
   * @param {string} eventName - The socket event name
   * @param {string} status - 'success' or 'error'
   * @returns {boolean}
   */
  shouldNotify(eventName, status) {
    // ========================================
    // SILENT EVENTS (Never show notifications)
    // ========================================

    // Data loading events (too noisy)
    if (eventName.endsWith('_loaded')) return false;
    if (eventName.endsWith('_loading')) return false;
    if (eventName.startsWith('get_')) return false;

    // Connection/auth events (handled separately)
    if (eventName === 'connection_established') return false;
    if (eventName === 'authentication_failed') return false;

    // List/pagination events (too noisy)
    if (eventName.includes('_list')) return false;
    if (eventName.includes('pagination')) return false;

    // Status check events (informational only)
    if (eventName.includes('_status') && status === 'success') return false;

    // ========================================
    // ALWAYS SHOW (Critical events)
    // ========================================

    // ALWAYS show errors
    if (status === 'error') return true;

    // ALWAYS show mutations (creates, updates, deletes)
    if (eventName.endsWith('_created')) return true;
    if (eventName.endsWith('_updated')) return true;
    if (eventName.endsWith('_deleted')) return true;

    // ALWAYS show sent/posted actions
    if (eventName.endsWith('_sent')) return true;
    if (eventName.endsWith('_posted')) return true;

    // Partner/collaboration events (important)
    if (eventName.includes('partner_') && !eventName.includes('_loaded')) return true;
    if (eventName.includes('collaboration_')) return true;

    // Connection management events
    if (eventName.includes('connection_request')) return true;
    if (eventName.includes('invited')) return true;

    // ========================================
    // DEFAULT: Don't show
    // ========================================
    return false;
  }

  /**
   * Determine notification type based on status and event name
   * @param {string} status - 'success' or 'error'
   * @param {string} eventName - The event name
   * @returns {string} - 'success' | 'error' | 'warning' | 'info'
   */
  getNotificationType(status, eventName) {
    // Errors are always error type
    if (status === 'error') return 'error';

    // Deletions are success but could be warning if preferred
    if (eventName.includes('delete') || eventName.includes('removed')) {
      return 'success';
    }

    // Invitations/connection requests could be info
    if (eventName.includes('invited') || eventName.includes('connection_request')) {
      return 'info';
    }

    // Default success for all other successful operations
    return 'success';
  }

  /**
   * Format notification message from event data
   * @param {string} eventName - The event name
   * @param {string} message - Backend message
   * @param {object} data - Event data
   * @returns {string}
   */
  formatMessage(eventName, message, data) {
    // If backend provides a good message, use it
    if (message && message.length > 10) {
      return message;
    }

    // Otherwise, create a nice message from the event name
    // Convert: 'customer_created' → 'Customer created successfully'
    const parts = eventName.split('_');

    // Remove 'failed' from error events
    const cleaned = parts.filter(p => p !== 'failed');

    // Get entity and action
    const entity = this.capitalize(cleaned[0]);
    const action = cleaned[cleaned.length - 1];

    // Create message
    return `${entity} ${action} successfully`;
  }

  /**
   * Get notification title based on event
   * @param {string} eventName - The event name
   * @param {string} type - Notification type
   * @returns {string|null}
   */
  getTitle(eventName, type) {
    if (type === 'error') {
      return 'Error';
    }

    // Most notifications don't need titles, just return null
    // The message is usually sufficient
    return null;
  }

  /**
   * Determine how long notification should stay visible
   * @param {string} type - Notification type
   * @returns {number|null} - Duration in ms, or null for manual dismiss
   */
  getDuration(type) {
    switch (type) {
      case 'error':
        return null; // Errors stay until dismissed
      case 'warning':
        return 6000; // 6 seconds
      case 'success':
        return 4000; // 4 seconds
      case 'info':
        return 5000; // 5 seconds
      default:
        return 4000;
    }
  }

  /**
   * Capitalize first letter
   * @param {string} str
   * @returns {string}
   */
  capitalize(str) {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  /**
   * Debug logging
   */
  log(message, ...args) {
    if (this.debug) {
      console.log(`[NotificationListener] ${message}`, ...args);
    }
  }

  /**
   * Cleanup (if needed)
   */
  cleanup() {
    if (this.socketManager?.universalHandler) {
      // Note: We're listening to 'response' which is a catch-all
      // If we need to remove the listener, we'd need to store the bound function
      this.log('🧹 NotificationListener: Cleanup');
    }
    this.isInitialized = false;
  }
}

// Export initialization function (to match store initialization pattern)
export const initializeNotificationListener = (socketManager) => {
  if (!socketManager) {
    console.error('❌ initializeNotificationListener: No socket manager provided');
    return null;
  }

  const listener = new NotificationListener(socketManager);
  listener.initialize();

  return listener;
};

export default NotificationListener;
